import React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { navigate } from 'gatsby';

const AuthContext = createContext<{ user: object | null; setUser: (user: object | null) => void; login: (token: string, user: object) => void; logout: () => Promise<boolean>; userStats: UserStats | null; refreshUserStats: () => Promise<UserStats | null>; checkUserStats: () => void; userChats: object | null; refreshUserChats: () => Promise<object | null>; deleteChat: (chatUuid: string) => void; apiRequestAuthenticated: ({ method, url, body }: { method: string, url: string, body?: any }) => Promise<Response | undefined>; apiRequest: ({ method, url, body }: { method: string, url: string, body?: any }) => Promise<Response | undefined> }>({
  user: null,
  setUser: () => { },
  login: () => { },
  logout: () => Promise.resolve(false),
  userStats: null,
  refreshUserStats: () => Promise.resolve(null),
  checkUserStats: () => { },
  userChats: null,
  refreshUserChats: () => Promise.resolve(null),
  deleteChat: () => { },
  apiRequestAuthenticated: () => Promise.resolve(undefined),
  apiRequest: () => Promise.resolve(undefined),
});

interface UserStats {
  chatsDay: number;
  chatsLimitDay: number;
  chatsMonth: number;
  chatsLimitMonth: number;
  totalLimitExceeded: boolean;
}

interface UserChats {
  chats: object[];
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<object | null>(null);
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [userChats, setUserChats] = useState<UserChats | null>(null);

  function login(token: string, user: object) {
    localStorage.setItem("authToken", token);
    localStorage.setItem("user", JSON.stringify(user));
    setUser(user);
  }

  async function logout(path?: string): Promise<boolean> {
    localStorage.removeItem("authToken");
    localStorage.removeItem("user");
    setUser(null);
    refreshUserStats();
    if (path) navigate(path);
    return true;
  }

  useEffect(() => {
    const stored = localStorage.getItem("user");
    if (stored) setUser(JSON.parse(stored));
  }, []);

  function getToken() {
    const token = localStorage.getItem("authToken");
    // if (!token) {
    //   throw new Error("Токен не найден. Пользователь не авторизован.");
    // }
    return token;
  }

  async function apiRequest({ method, url, body }: { method: string, url: string, body?: any }) {
    const res = await fetch(`${process.env.GATSBY_API_BASE_URL}/${url}`, {
      method,
      headers: {
        "Content-Type": "application/json",
      },
      body: body ? JSON.stringify(body) : undefined,
      credentials: "include",
    });
    if (!res.ok) {
      throw new Error(`Ошибка сервера: ${res.status}`);
    }
    return res;
  }

  async function apiRequestAuthenticated({ method, url, body }: { method: string, url: string, body?: any }) {
    const token = getToken();
    if (!token) return;

    const res = await fetch(`${process.env.GATSBY_API_BASE_URL}/${url}`, {
      method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: body ? JSON.stringify(body) : undefined,
      credentials: "include",
    });
    if (!res.ok) {
      throw new Error(`Ошибка сервера: ${res.status}`);
    }
    return res;
  }

  async function refreshUserStats(): Promise<UserStats | null> {
    if (!user) {
      setUserStats(null);
      return null;
    }
    try {
      const res = await apiRequestAuthenticated({
        method: "GET",
        url: `user/stats`,
      });
      if (!res) return null;
      const data = await res.json();
      setUserStats(data);
      return data;
    } catch (err) {
      console.error("Ошибка при запросе к /user/stats:", err);
      return null;
    }
  }

  async function checkUserStats() {
    const res = await refreshUserStats();
    if (!res) return false;
    if (res.chatsDay >= res.chatsLimitDay) {
      navigate("/limit?type=day");
      return false;
    }
    if (res.chatsMonth >= res.chatsLimitMonth) {
      navigate("/limit?type=month");
      return false;
    }
    if (res.totalLimitExceeded !== false) {
      navigate("/limit?type=total");
      return false;
    }
    return true;
  }

  async function refreshUserChats(): Promise<UserChats | null> {
    if (!user) {
      setUserChats(null);
      return null;
    }

    const CHATS_LIMIT = 100;
    try {
      const res = await apiRequestAuthenticated({
        method: "GET",
        url: `chats?limit=${CHATS_LIMIT}`,
      });

      if (!res) return null;
      const data = await res.json();
      const chatsArr = data?.chats || "Ошибка. Чаты отсутствуют";
      setUserChats(chatsArr);
      return chatsArr;
    } catch (err) {
      console.error("Ошибка при запросе к /chats:", err);
      return null;
    }
  }

  async function deleteChat(chatUuid: string) {
    try {
      await apiRequestAuthenticated({
        method: "DELETE",
        url: `chats/${chatUuid}`,
      });
    } catch (err) {
      console.error("Ошибка при удалении чата:", err);
    }
  }

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, userStats, refreshUserStats, checkUserStats, userChats, refreshUserChats, deleteChat, apiRequestAuthenticated, apiRequest }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
